import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useCashier,
  usePatchCashier,
  useRotatePin,
  useSoftDeleteCashier,
  type Cashier,
  type CashierPinRotated,
} from '@/hooks/use-employees';
import { useRoles } from '@/hooks/use-roles';
import { OneTimePinModal } from './one-time-pin-modal';
import { isCashierEditable } from './cashier-ownership';

import { CashierGpStores } from './cashier-gp-stores';
import { CashierLoginQrButton } from './cashier-login-qr';
import { CashierFiscalPanel } from '@/pages/fiscal-cabinet/fiscal-record-panels';
import { useT } from '@/i18n/use-t';
import { useHasPermission } from '@/auth/auth-store';

export function CashierDetailPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const cashierQuery = useCashier(id);
  const rolesQuery = useRoles();
  const patch = usePatchCashier();
  const rotatePin = useRotatePin();
  const softDelete = useSoftDeleteCashier();

  const [displayName, setDisplayName] = useState('');
  const [loginName, setLoginName] = useState('');
  const [editing, setEditing] = useState(false);
  const [pinFor, setPinFor] = useState<{ pin: string; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync local edit state to the server-loaded cashier. RL-371: key on the
  // record id and the edited fields' server values, never on the query's
  // object identity — every refetch (window refocus, an unrelated mutation)
  // yields a new object and would wipe what the operator is typing. A
  // genuine upstream change to one of these fields still wins.
  const serverId = cashierQuery.data?.id;
  const serverDisplayName = cashierQuery.data?.displayName;
  const serverLoginName = cashierQuery.data?.loginName;
  useEffect(() => {
    if (serverId === undefined) return;
    setDisplayName(serverDisplayName ?? '');
    setLoginName(serverLoginName ?? '');
  }, [serverId, serverDisplayName, serverLoginName]);

  if (id === undefined) return null;
  const cashier = cashierQuery.data;
  if (cashierQuery.isLoading)
    return (
      <p className="text-sm text-muted-foreground">
        {t('employees.detail.loading', 'Loading cashier…')}
      </p>
    );
  if (cashier === undefined)
    return (
      <p className="text-sm text-destructive">
        {t('employees.detail.notFound', 'Cashier not found.')}
      </p>
    );

  // VL-351: a cashier the store system owns is read-only here; the API
  // refuses every change to it, so the controls are disabled rather than
  // offered and then refused.
  const readOnly = !isCashierEditable(cashier);

  async function handleSaveDisplay(): Promise<void> {
    setError(null);
    try {
      await patch.mutateAsync({ id: id!, patch: { displayName } });
      setEditing(false);
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleSaveLoginName(): Promise<void> {
    setError(null);
    try {
      const trimmed = loginName.trim();
      await patch.mutateAsync({ id: id!, patch: { loginName: trimmed === '' ? null : trimmed } });
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleToggleStatus(next: 'active' | 'suspended'): Promise<void> {
    setError(null);
    try {
      await patch.mutateAsync({ id: id!, patch: { status: next } });
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleChangeRole(nextRoleId: string | null): Promise<void> {
    setError(null);
    try {
      await patch.mutateAsync({ id: id!, patch: { roleId: nextRoleId } });
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleToggleAttendant(next: boolean): Promise<void> {
    setError(null);
    try {
      await patch.mutateAsync({ id: id!, patch: { isAttendant: next } });
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleRotatePin(): Promise<void> {
    setError(null);
    try {
      const result: CashierPinRotated = await rotatePin.mutateAsync({ id: id! });
      setPinFor({
        pin: result.pin,
        label: `${cashier!.displayName} (${cashier!.employeeCode})`,
      });
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  async function handleSoftDelete(): Promise<void> {
    setError(null);
    try {
      await softDelete.mutateAsync(id!);
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">
            <Link to="/employees" className="hover:underline">
              {t('employees.list.title', 'Cashiers')}
            </Link>{' '}
            / <span className="font-mono">{cashier.employeeCode}</span>
          </p>
          <h1 className="mt-1 text-2xl font-semibold">{cashier.displayName}</h1>
        </div>
        <StatusBadge cashier={cashier} />
      </header>

      {cashier.managedBy !== undefined && cashier.managedBy !== null && (
        <section
          role="status"
          className="space-y-1 rounded-card border border-border bg-secondary/40 p-5"
        >
          <h2 className="text-sm font-medium">
            {t('employees.managed.title', 'Managed by the store system')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('employees.managed.source', 'Source: {source}').replace(
              '{source}',
              cashier.managedBy.sourceName,
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              'employees.managed.help',
              'This cashier is imported from the store system and is read-only here. Change it in the store system; the change arrives with the next import.',
            )}
          </p>
        </section>
      )}

      {/* ---------- Store + store group (VL-350) ---------- */}

      <section className="space-y-2 rounded-card border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium">{t('employees.list.store', 'Store')}</h2>
          <span className="text-sm">
            {cashier.location === undefined || cashier.location === null ? (
              <span className="text-muted-foreground">
                {t('employees.list.noStore', 'Not bound to a store')}
              </span>
            ) : (
              <>
                <span className="font-mono">{cashier.location.code}</span> {cashier.location.name}
              </>
            )}
          </span>
        </div>
        {cashier.storeGroup !== undefined && cashier.storeGroup !== null && (
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-medium">
              {t('employees.detail.storeGroup', 'Store system group')}
            </h2>
            <span className="font-mono text-sm">{cashier.storeGroup}</span>
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-card border border-border bg-card p-5">
        <h2 className="text-sm font-medium">{t('employees.gp_stores.title', 'GP stores')}</h2>
        <CashierGpStores cashier={cashier} />
        <p className="text-xs text-muted-foreground">
          {t(
            'employees.gp_stores.help',
            'Assignments supplied by GP. Manage changes in the source system.',
          )}
        </p>
      </section>

      {/* ---------- Display data ---------- */}

      <section className="space-y-3 rounded-card border border-border bg-card p-5">
        <h2 className="text-sm font-medium">{t('employees.field.displayName', 'Display name')}</h2>
        {!editing ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-base">{cashier.displayName}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={readOnly}
            >
              {t('common.edit', 'Edit')}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="displayName" className="sr-only">
              {t('employees.field.displayName', 'Display name')}
            </Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSaveDisplay} disabled={patch.isPending}>
                {t('common.save', 'Save')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(false);
                  setDisplayName(cashier.displayName);
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-card border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-medium">{t('employees.field.loginName', 'QR login name')}</h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'employees.detail.loginNameHelp',
              "Exact GP cashierloginname encoded in this cashier's Yuki QR badge.",
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="loginName" className="sr-only">
            {t('employees.field.loginName', 'QR login name')}
          </Label>
          <Input
            id="loginName"
            value={loginName}
            onChange={(e) => setLoginName(e.target.value)}
            maxLength={120}
            placeholder={t('employees.detail.loginNamePlaceholder', 'Not assigned')}
            disabled={readOnly}
          />
          <Button
            variant="outline"
            onClick={handleSaveLoginName}
            disabled={readOnly || patch.isPending || loginName.trim() === (cashier.loginName ?? '')}
          >
            {t('common.save', 'Save')}
          </Button>
        </div>
      </section>

      {/* ---------- Status ---------- */}

      <section className="flex items-center justify-between gap-4 rounded-card border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-medium">{t('employees.field.status', 'Status')}</h2>
          <p className="text-sm text-muted-foreground">
            {cashier.status === 'active'
              ? t('employees.detail.statusActive', 'Cashier can log in to terminals.')
              : t(
                  'employees.detail.statusSuspended',
                  'Cashier cannot log in. Terminals will refuse PIN entry with a "suspended" message.',
                )}
          </p>
        </div>
        {cashier.status === 'active' ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={readOnly || patch.isPending}>
                {t('employees.detail.suspend', 'Suspend')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('employees.detail.confirmSuspendTitle', 'Suspend {name}?').replace(
                    '{name}',
                    cashier.displayName,
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    'employees.detail.confirmSuspendBody',
                    'Once the next sync lands on terminals, this cashier will be unable to start new shifts. Any in-progress shift is unaffected until close. Reactivation is a click away.',
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleToggleStatus('suspended')}>
                  {t('employees.detail.suspend', 'Suspend')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button
            onClick={() => handleToggleStatus('active')}
            disabled={readOnly || patch.isPending}
          >
            {t('employees.detail.reactivate', 'Reactivate')}
          </Button>
        )}
      </section>

      {/* ---------- Role ---------- */}

      <section className="space-y-3 rounded-card border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-medium">{t('employees.field.role', 'Role')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('employees.detail.roleHelpBefore', 'Assigns the cashier a role from the')}{' '}
            <Link to="/roles" className="hover:underline">
              {t('roles.title', 'Roles')}
            </Link>{' '}
            {t(
              'employees.detail.roleHelpAfter',
              'section, which drives their effective permissions. Roles are edited in that section; here you only choose which one applies.',
            )}
          </p>
          {cashier.roleReadOnly === true && !readOnly && (
            <p className="text-sm text-muted-foreground">
              {t(
                'employees.managed.roleLocked',
                'This imported cashier’s role still follows the store system. The source is disabled, but a role changed here would remain overridden after it is enabled again.',
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="cashier-role" className="sr-only">
            {t('employees.field.role', 'Role')}
          </Label>
          <Select
            id="cashier-role"
            className="max-w-xs"
            value={cashier.role?.id ?? ''}
            disabled={
              readOnly || cashier.roleReadOnly === true || patch.isPending || rolesQuery.isLoading
            }
            onChange={(e) => handleChangeRole(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">{t('employees.detail.noRole', 'No role')}</option>
            {(rolesQuery.data ?? []).map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
                {role.isBuiltIn ? ` (${t('roles.builtIn', 'built-in')})` : ''}
              </option>
            ))}
          </Select>
          {cashier.role !== null && <Badge variant="outline">{cashier.role.name}</Badge>}
        </div>
      </section>

      {/* ---------- SCO attendant ---------- */}

      <section className="flex items-center justify-between gap-4 rounded-card border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-medium">
            {t('employees.detail.scoAttendant', 'SCO attendant')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {cashier.isAttendant
              ? t(
                  'employees.detail.attendantOn',
                  'Can act as a self-checkout attendant — approves SCO interventions.',
                )
              : t(
                  'employees.detail.attendantOff',
                  'Cannot approve self-checkout interventions. Enable to allow this cashier to attend SCO lanes.',
                )}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => handleToggleAttendant(!cashier.isAttendant)}
          disabled={readOnly || patch.isPending}
        >
          {cashier.isAttendant
            ? t('employees.detail.revokeAttendant', 'Revoke attendant')
            : t('employees.detail.makeAttendant', 'Make attendant')}
        </Button>
      </section>

      {/* ---------- PIN ---------- */}

      <section className="flex items-center justify-between gap-4 rounded-card border border-border bg-card p-5">
        <div>
          <h2 className="text-sm font-medium">{t('employees.field.pin', 'PIN')}</h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'employees.detail.pinHelp',
              'Last updated {when}. Reset count: {count}. Rotating issues a new cleartext PIN shown exactly once.',
            )
              .replace('{when}', relative(cashier.pinUpdatedAt, t))
              .replace('{count}', String(cashier.pinResetCount))}
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              disabled={readOnly || rotatePin.isPending || cashier.status === 'suspended'}
            >
              {t('employees.detail.rotatePin', 'Rotate PIN')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('employees.detail.confirmRotateTitle', 'Rotate PIN for {name}?').replace(
                  '{name}',
                  cashier.displayName,
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  'employees.detail.confirmRotateBody1',
                  'The current PIN will stop working immediately on every terminal once the next sync lands. A new cleartext PIN is shown',
                )}{' '}
                <strong>{t('employees.pin.exactlyOnce', 'exactly once')}</strong>{' '}
                {t(
                  'employees.detail.confirmRotateBody2',
                  'after rotation — hand it to the cashier physically.',
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRotatePin}>
                {t('employees.detail.rotatePin', 'Rotate PIN')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      {/* VL-349 — login QR code; renders only for a holder of employees.admin. */}
      <CashierLoginQrSection cashier={cashier} />

      {/* PFC-10 — renders only for a fiscal-cabinet tenant and a user with fiscal.cabinet.write. */}
      <CashierFiscalPanel cashierId={cashier.id} />

      {/* ---------- Delete (soft) ---------- */}

      {cashier.status === 'active' && !readOnly && (
        <section className="rounded-card border border-destructive/30 bg-destructive/5 p-5">
          <h2 className="text-sm font-medium text-destructive">
            {t('employees.detail.dangerZone', 'Danger zone')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'employees.detail.dangerHelp',
              'Removing a cashier soft-deletes (sets status to suspended). Audit log linkage is preserved — cashier IDs are referenced by past transactions, so we never hard-delete.',
            )}
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="mt-3">
                {t('employees.detail.remove', 'Remove cashier')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('employees.detail.confirmRemoveTitle', 'Remove {name}?').replace(
                    '{name}',
                    cashier.displayName,
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    'employees.detail.confirmRemoveBody',
                    "Soft-delete: the cashier is set to suspended and can't log in. To restore, reactivate from the status section above.",
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className={cn(buttonVariants({ variant: 'destructive' }))}
                  onClick={handleSoftDelete}
                >
                  {t('employees.detail.removeAction', 'Remove')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {pinFor !== null && (
        <OneTimePinModal
          open={true}
          pin={pinFor.pin}
          cashierLabel={pinFor.label}
          title={t('employees.pin.rotatedTitle', 'PIN rotated')}
          onAcknowledge={() => setPinFor(null)}
        />
      )}
    </div>
  );
}

function CashierLoginQrSection({ cashier }: { cashier: Cashier }) {
  const t = useT();
  const allowed = useHasPermission('employees.admin');
  if (!allowed) return null;
  return (
    <section className="flex items-center justify-between gap-4 rounded-card border border-border bg-card p-5">
      <div>
        <h2 className="text-sm font-medium">{t('employees.loginQr.action', 'Login QR code')}</h2>
        <p className="text-sm text-muted-foreground">
          {t(
            'employees.loginQr.help',
            'A personal code the cashier scans on the till to sign in without a PIN. Valid until regenerated.',
          )}
        </p>
      </div>
      <CashierLoginQrButton cashier={cashier} size="default" />
    </section>
  );
}

function StatusBadge({ cashier }: { cashier: Cashier }) {
  const t = useT();
  if (cashier.status === 'active')
    return <Badge variant="success">{t('employees.status.active', 'active')}</Badge>;
  return <Badge variant="outline">{t('employees.status.suspended', 'suspended')}</Badge>;
}

type Translate = (key: string, bakedEnglish?: string) => string;

function relative(at: string | null, t: Translate): string {
  if (at === null) return t('employees.relative.never', 'never');
  const ms = Date.now() - new Date(at).getTime();
  const n = (value: number): string => String(Math.round(value));
  if (ms < 60_000) return t('employees.relative.seconds', '{n}s ago').replace('{n}', n(ms / 1000));
  if (ms < 3_600_000)
    return t('employees.relative.minutes', '{n}m ago').replace('{n}', n(ms / 60_000));
  if (ms < 86_400_000)
    return t('employees.relative.hours', '{n}h ago').replace('{n}', n(ms / 3_600_000));
  return t('employees.relative.days', '{n}d ago').replace('{n}', n(ms / 86_400_000));
}

function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return t('employees.error.unexpected', 'Unexpected error');
}
